import { useEffect, useRef } from 'react';
import { off, on } from '@crehana/utils';
var defaultEvents = ['mousedown', 'touchstart'];

/**
 * React UI hook that triggers a callback when user clicks outside the target element.
 *
 * Usage:
 * ```tsx
 * import { useRef } from 'react';
 * import useClickAway from '@crehana/react-hooks/dist/useClickAway';
 *
 * const Demo = () => {
 *   const ref = useRef(null);
 *
 *   useClickAway(ref, () => {
 *     console.log('OUTSIDE CLICKED');
 *   });
 *
 *   return (
 *     <div ref={ref} style={{
 *       width: 200,
 *       height: 200,
 *       background: 'red',
 *     }} />
 *   );
 * };
 * ```
 *
 * Reference:
 * ```js
 * useClickAway(ref, onMouseEvent)
 * useClickAway(ref, onMouseEvent, ['click'])
 * useClickAway(ref, onMouseEvent, ['mousedown', 'touchstart'])
 * ```
 *
 * {@link https://github.com/streamich/react-use/blob/master/docs/useClickAway.md | useClickAway by react-use}
 */
var useClickAway = function useClickAway(ref, onClickAway) {
  var events = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : defaultEvents;
  var savedCallback = useRef(onClickAway);
  useEffect(function () {
    savedCallback.current = onClickAway;
  }, [onClickAway]);
  useEffect(function () {
    // @ts-ignore
    var handler = function handler(event) {
      var el = ref.current;
      el && !el.contains(event.target) && savedCallback.current(event);
    };
    events.forEach(function (eventName) {
      on(document, eventName, handler);
    });
    return function () {
      events.forEach(function (eventName) {
        off(document, eventName, handler);
      });
    };
  }, [events, ref]);
};
export default useClickAway;